import axiosClient from "api/axiosInstance";
import {
  CreateProductionPrognoseRequestDto,
  CreateProductionPrognoseResponseDto,
  PreviousDayPrognoseChartData,
  ProductionForecast,
  ProductionPrognoseSelect,
  UpdateProductionPrognosisDto,
  ProductionForecastGroups,
  ProductionForecastGroup,
} from "api/models/ProductionForecast";

export const getProductionPrognose = async (
  id: number,
): Promise<ProductionForecast> => {
  const client = axiosClient();
  const response = await client.get(`/production-prognosis/${id}`);
  return response.data;
};

export const createProductionPrognose = async (
  data: CreateProductionPrognoseRequestDto,
): Promise<CreateProductionPrognoseResponseDto> => {
  const client = axiosClient();
  const response = await client.post(`/production-prognosis`, data);
  return response.data;
};

export const getProductionPrognoses = async (
  date?: string,
): Promise<ProductionPrognoseSelect> => {
  const client = axiosClient();
  const response = await client.get(`/production-prognosis`, {
    params: { date },
  });
  return response.data;
};

export const updateProductionPrognose = async (
  id: number,
  data: UpdateProductionPrognosisDto,
): Promise<UpdateProductionPrognosisDto> => {
  const client = axiosClient();
  const response = await client.patch(`/production-prognosis/${id}`, data);
  return response.data;
};

export const verifyProductionPrognose = async (id: number) => {
  const client = axiosClient();
  const response = await client.put(`/production-prognosis/${id}/verify`);
  return response.data;
};

export const unverifyProductionPrognose = async (id: number) => {
  const client = axiosClient();
  const response = await client.put(`/production-prognosis/${id}/unverify`);
  return response.data;
};

export const getProductionForecastExport = async (
  forecastDate: string,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.get(
    `/production-prognosis/export?date=${forecastDate}`,
    {
      responseType: "blob",
    },
  );

  return response.data;
};

export const getPastProductionForecastChartData = async (
  forecastDate: string,
  forecastType: string,
  priceArea: string,
): Promise<PreviousDayPrognoseChartData> => {
  const client = axiosClient();
  const response = await client.get(`/production-prognosis/prognosis-data`, {
    params: {
      date: forecastDate,
      type: forecastType,
      priceArea,
    },
  });

  return response.data;
};

export const updateProductionForecastData = async (
  forecastDate: string,
  forecastType: string,
): Promise<ProductionForecast> => {
  const client = axiosClient();
  const response = await client.patch(
    `/production-prognosis/update-prognose-data`,
    null,
    {
      params: {
        date: forecastDate,
        type: forecastType,
      },
    },
  );
  return response.data.data;
};

export const getProductionForecastGroups = async (
  fromDate: string,
  toDate: string,
): Promise<ProductionForecastGroups> => {
  const client = axiosClient();
  const response = await client.get(
    `/production-forecasts/groups?fromDate=${fromDate}&toDate=${toDate}`,
  );
  return response.data;
};

export const getProductionForecastGroup = async (
  id: number,
  filters: any,
  includeRealProduction?: boolean,
): Promise<ProductionForecastGroup> => {
  const client = axiosClient();

  const response = await client.get(`/production-forecasts/groups/${id}`, {
    params: {
      fullData: true,
      ...(includeRealProduction && { includeRealProduction: true }),
      ...(filters.priceArea && {
        priceArea: filters.priceArea,
      }),
      ...(filters.productionParkType && {
        productionParkType: filters.productionParkType,
      }),
      ...(filters.clientIds &&
        filters.clientIds.length > 0 && {
          clientIds: filters.clientIds.join(","),
        }),
      ...(filters.productionParkIds &&
        filters.productionParkIds.length > 0 && {
          productionParkIds: filters.productionParkIds.join(","),
        }),
    },
  });
  return response.data;
};

export const getProductionForecastAbsoluteErrors = async (
  filters: any,
): Promise<ProductionForecastGroup> => {
  const client = axiosClient();

  const response = await client.get(`/production-forecasts/errors/last-month`, {
    params: {
      ...(filters.priceArea &&
        filters.priceArea && {
          priceArea: filters.priceArea,
        }),
      ...(filters.productionParkType &&
        filters.productionParkType && {
          productionParkType: filters.productionParkType,
        }),
      ...(filters.clientIds &&
        filters.clientIds.length > 0 && {
          clientIds: filters.clientIds.join(","),
        }),
      ...(filters.productionParkIds &&
        filters.productionParkIds.length > 0 && {
          productionParkIds: filters.productionParkIds.join(","),
        }),
    },
  });
  return response.data;
};

export const getProductionForecast = async (
  id: number,
): Promise<ProductionForecastGroup> => {
  const client = axiosClient();
  const response = await client.get(`/production-forecasts/${id}`);
  return response.data;
};

export const exportProductionForecastGroup = async (
  id: number,
  filters: any,
): Promise<ProductionForecastGroup> => {
  const client = axiosClient();
  const response = await client.get(
    `/production-forecasts/groups/${id}/export`,
    {
      params: {
        ...(filters.priceArea && {
          priceArea: filters.priceArea,
        }),
        ...(filters.productionParkType && {
          productionParkType: filters.productionParkType,
        }),
        ...(filters.clientIds &&
          filters.clientIds.length > 0 && {
            clientIds: filters.clientIds.join(","),
          }),
        ...(filters.productionParkIds &&
          filters.productionParkIds.length > 0 && {
            productionParkIds: filters.productionParkIds.join(","),
          }),
      },
      responseType: "blob",
    },
  );
  return response.data;
};

export const updateProductionForecast = async (
  id: number,
  postData: any,
): Promise<ProductionForecast> => {
  const client = axiosClient();
  const response = await client.put(`/production-forecasts/${id}`, postData);
  return response.data;
};

export const createProductionForecasts = async (
  fromDate: string,
): Promise<ProductionForecast> => {
  const client = axiosClient();
  const response = await client.post(
    `/production-forecasts?fromDate=${fromDate}`,
  );
  return response.data;
};
